import { parseDate } from '@internationalized/date';

/**
 * Creates a timezone-safe Date object from date parts
 * Always creates the date in UTC to avoid timezone shifts
 */
const createSafeDate = (year: number, month: number, day: number): Date => {
  return new Date(Date.UTC(year, month - 1, day));
};

/**
 * Parses a date string safely without timezone conversion
 * Treats YYYY-MM-DD as intended date regardless of timezone
 */
const parseDateStringSafe = (dateStr: string): Date => {
  // For YYYY-MM-DD format, parse it manually to avoid timezone issues
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (match) {
    const year = parseInt(match[1], 10);
    const month = parseInt(match[2], 10);
    const day = parseInt(match[3], 10);
    return createSafeDate(year, month, day);
  }

  // Fallback for other formats - but this is risky for timezone conversion
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) {
    throw new Error(`Invalid date string: ${dateStr}`);
  }
  return date;
};

/**
 * Formats a date object or string into a human-readable format
 * Uses timezone-safe date parsing to ensure consistency
 */
export const formatDateForDisplay = (date: any): string => {
  try {
    let dateObj;

    if (typeof date === 'string') {
      // Handle ISO format or YYYY-MM-DD with timezone safety
      dateObj = parseDateStringSafe(date);
    } else if (
      date &&
      typeof date === 'object' &&
      'year' in date &&
      'month' in date &&
      'day' in date
    ) {
      // Handle date object with year/month/day properties - use UTC to avoid timezone shifts
      dateObj = createSafeDate(date.year, date.month, date.day);
    } else {
      throw new Error('Invalid date format');
    }

    if (isNaN(dateObj.getTime())) {
      throw new Error('Invalid date');
    }

    // Use UTC methods to avoid timezone conversion during formatting
    const utcDay = dateObj.getUTCDate();
    const utcMonth = dateObj.getUTCMonth();
    const utcYear = dateObj.getUTCFullYear();

    // If it's the first day of the month, only show month and year
    if (utcDay === 1) {
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC' // Force UTC to prevent timezone conversion
      }).format(dateObj);
    }

    // Otherwise show full date
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC' // Force UTC to prevent timezone conversion
    }).format(dateObj);
  } catch (error) {
    console.error('Error formatting date:', error, date);
    return 'Invalid Date';
  }
};

/**
 * Formats a date range object into a human-readable string
 * Uses timezone-safe formatting to ensure consistency
 */
export const formatDateRange = (range: any): string => {
  if (!range || !range.start || !range.end) return 'Select Date Range';

  try {
    // Handle different possible formats
    let startDate, endDate;

    if (typeof range.start === 'string') {
      // If it's a string representation
      startDate = range.start;
      endDate = range.end;
    } else {
      // If it's an object from the date picker - use consistent YYYY-MM-DD format
      startDate = `${range.start.year}-${String(range.start.month).padStart(
        2,
        '0'
      )}-${String(range.start.day).padStart(2, '0')}`;
      endDate = `${range.end.year}-${String(range.end.month).padStart(
        2,
        '0'
      )}-${String(range.end.day).padStart(2, '0')}`;
    }

    return `${formatDateForDisplay(startDate)} - ${formatDateForDisplay(
      endDate
    )}`;
  } catch (error) {
    console.error('Error formatting date range:', error);
    return 'Select Date Range';
  }
};

/**
 * Parses a date string or value into a date object
 */
export const parseDateValue = (value: any) => {
  if (!value) return null;

  try {
    // Try to parse the value (could be a string date)
    if (typeof value === 'string') {
      if (value.includes('T') || value.includes('-')) {
        // ISO format or YYYY-MM-DD
        return parseDate(value);
      }
    }
    return null;
  } catch (error) {
    console.error('Error parsing date value:', error);
    return null;
  }
};

/**
 * Parses a date range string or value into a date range object
 * Uses timezone-safe parsing to ensure consistency
 */
export const parseDateRangeValue = (value: any) => {
  if (!value) return null;

  try {
    const parsedValue = typeof value === 'string' ? JSON.parse(value) : value;

    // Convert string dates to CalendarDate objects
    if (parsedValue && parsedValue.start && parsedValue.end) {
      return {
        start:
          typeof parsedValue.start === 'string'
            ? parseDate(parsedValue.start)
            : parsedValue.start,
        end:
          typeof parsedValue.end === 'string'
            ? parseDate(parsedValue.end)
            : parsedValue.end
      };
    }
    return null;
  } catch (error) {
    console.error('[parseDateRangeValue] Error parsing value:', value, error);
    return null;
  }
};

/**
 * Converts a CalendarDate object to a timezone-safe ISO date string (YYYY-MM-DD)
 * This ensures the date is exactly what the user selected, regardless of timezone
 */
export const calendarDateToISOString = (calendarDate: any): string => {
  if (!calendarDate || typeof calendarDate !== 'object') {
    throw new Error('Invalid CalendarDate object');
  }

  const { year, month, day } = calendarDate;
  if (!year || !month || !day) {
    throw new Error('CalendarDate must have year, month, and day properties');
  }

  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(
    2,
    '0'
  )}`;
};

/**
 * Validates that a date string is in YYYY-MM-DD format and represents a valid date
 */
export const isValidDateString = (dateStr: string): boolean => {
  if (!dateStr || typeof dateStr !== 'string') return false;

  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return false;

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);

  // Basic validation
  if (year < 1900 || year > 2100) return false;
  if (month < 1 || month > 12) return false;
  if (day < 1 || day > 31) return false;

  // Create a date and check if it's valid
  try {
    const date = createSafeDate(year, month, day);
    return (
      !isNaN(date.getTime()) &&
      date.getUTCFullYear() === year &&
      date.getUTCMonth() === month - 1 &&
      date.getUTCDate() === day
    );
  } catch {
    return false;
  }
};

/**
 * Creates a timezone-safe date string for API consumption
 * Ensures dates are sent exactly as the user intended
 */
export const createApiDateString = (
  year: number,
  month: number,
  day: number
): string => {
  if (
    !isValidDateString(
      `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(
        2,
        '0'
      )}`
    )
  ) {
    throw new Error(`Invalid date: ${year}-${month}-${day}`);
  }

  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(
    2,
    '0'
  )}`;
};
